/**
 * @fileoverview Floating menu component that combines date and aggregation filters
 * @module shared/components/filters/FlyingMenu
 */

import { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Calendar, CalendarClock, CalendarDays, Layers, Layers2 } from 'lucide-react'
import { DatePresetSelector } from './DatePresetSelector'
import { DateRangePicker } from '../ui/DateRangePicker'
import { AggregationSelector } from './AggregationSelector'
import { useFilterMenuSlot } from '@/shared/contexts/FilterMenuSlotContext'
import { useFilterSheet } from '@/shared/contexts/FilterSheetContext'
import { useFilter } from '@/shared/hooks/useFilter'
import { Toggle } from '@/components/ui/toggle'
import { Tooltip, TooltipTrigger } from '@/components/ui/tooltip'
import { Button, DarkTooltipContent } from '@/shared/components/ui'
import { MobileNavTrigger } from '@/shared/components/MobileNavTrigger'

/**
 * Floating menu component positioned at the top of the screen.
 * On mobile/tablet: includes hamburger trigger in same flex row.
 * On desktop: just the filter bar (sidebar handles navigation).
 *
 * @component
 * @example
 * ```tsx
 * <FlyingMenu />
 * ```
 */
export function FlyingMenu(): ReactElement {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { slot } = useFilterMenuSlot()
  const { open: openFilterSheet } = useFilterSheet()
  const { profitBasis, setProfitBasis } = useFilter()

  // Mobile/tablet filter bar with simple buttons that open the filter sheet
  const mobileFilterBar = (
    <div className="flex-1 bg-background/90 backdrop-blur-md border border-border/50 rounded-xl shadow-lg relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-white/5 to-white/10 pointer-events-none" />
      <div className="px-4 py-1.5 relative z-10">
        <div className="flex items-center justify-between gap-4">
          <img
            src="/abyssblock-icon.webp"
            alt={t('appHeader.logoAlt')}
            className="h-8 w-8 opacity-90 cursor-pointer"
            onClick={() => navigate('/dashboard')}
          />
          <div className="flex items-center gap-2 flex-nowrap overflow-x-auto whitespace-nowrap">
            {slot.content}
            {/* Trigger buttons - icon only on mobile, icon + text on tablet */}
            <Button
              variant="outline"
              className="glass-button !h-10 !px-2 md:!px-3 py-2"
              onClick={() => openFilterSheet('preset')}
            >
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="hidden md:inline text-xs text-muted-foreground">
                {t('filters.preset')}
              </span>
            </Button>
            <Button
              variant="outline"
              className="glass-button !h-10 !px-2 md:!px-3 py-2"
              onClick={() => openFilterSheet('range')}
            >
              <CalendarDays className="w-4 h-4 text-muted-foreground" />
              <span className="hidden md:inline text-xs text-muted-foreground">
                {t('filters.range')}
              </span>
            </Button>
            {!slot.hideAggregation && (
              <Button
                variant="outline"
                className="glass-button !h-10 !px-2 md:!px-3 py-2"
                onClick={() => openFilterSheet('aggregation')}
              >
                <CalendarClock className="w-4 h-4 text-muted-foreground" />
                <span className="hidden md:inline text-xs text-muted-foreground">
                  {t('filters.period')}
                </span>
              </Button>
            )}
            {!slot.hideProfitBasis && (
              <Button
                variant="outline"
                className="glass-button !h-10 !px-2 md:!px-3 py-2"
                onClick={() => openFilterSheet('profitBasis')}
              >
                {profitBasis === 'OC' ? (
                  <Layers className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <Layers2 className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="hidden md:inline text-xs text-muted-foreground">
                  {t('filters.costs')}
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  // Desktop filter bar with dropdown menus
  const desktopFilterBar = (
    <div className="flex-1 bg-background/90 backdrop-blur-md border border-border/50 rounded-xl shadow-lg relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-white/5 to-white/10 pointer-events-none" />
      <div className="px-4 py-2.5 relative z-10">
        <div className="flex items-center justify-between gap-4">
          <img
            src="/abyssblock-white.webp"
            alt={t('appHeader.logoAlt')}
            className="h-10 w-auto opacity-90 cursor-pointer"
            onClick={() => navigate('/dashboard')}
          />
          <div className="flex items-center gap-3 flex-nowrap overflow-x-auto whitespace-nowrap">
            {slot.content}
            <DatePresetSelector />
            <DateRangePicker />
            {!slot.hideAggregation && <AggregationSelector />}
            {!slot.hideProfitBasis &&
              (() => {
                const profitBasisLabel =
                  profitBasis === 'OC'
                    ? t('filters.afterBusinessCosts', { defaultValue: 'After business costs' })
                    : t('filters.beforeBusinessCosts', { defaultValue: 'Before business costs' })
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Toggle
                        pressed={profitBasis === 'OC'}
                        onPressedChange={pressed => setProfitBasis(pressed ? 'OC' : 'CM2')}
                        aria-label={profitBasisLabel}
                        variant="outline"
                        className="glass-button h-10 !px-3 py-2 text-muted-foreground data-[state=on]:bg-transparent data-[state=on]:text-muted-foreground"
                      >
                        {profitBasis === 'OC' ? (
                          <Layers className="w-4 h-4" />
                        ) : (
                          <Layers2 className="w-4 h-4" />
                        )}
                      </Toggle>
                    </TooltipTrigger>
                    <DarkTooltipContent>{profitBasisLabel}</DarkTooltipContent>
                  </Tooltip>
                )
              })()}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      className="sticky top-0 z-50 w-full"
    >
      {/* Subtle top scrim to improve contrast over content */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-24 z-40 pointer-events-none hidden dark:block bg-gradient-to-b from-black/80 to-transparent"
      />

      <div
        className={`mt-2 w-full ${slot.maxWidthClass ?? 'max-w-screen-xl'} mx-auto px-4 lg:px-8 relative z-50`}
      >
        {/* Mobile/Tablet: hamburger + filter bar with sheet triggers */}
        <div className="flex items-stretch gap-2 lg:hidden">
          <MobileNavTrigger />
          {mobileFilterBar}
        </div>

        {/* Desktop: filter bar with dropdown menus */}
        <div className="hidden lg:block">{desktopFilterBar}</div>
      </div>
    </motion.div>
  )
}
